import { useCallback, useEffect, useRef, useState } from 'react';

import { difference, equals } from '../../../../../common/maths/geometry/point';
import { Point } from '../../../../../common/maths/geometry/pointTypes';
import { RectSize } from '../../../../../common/maths/geometry/rect/rectTypes';
import useUpdatedRef from '../../../../utils/react/useUpdatedRef';
import { clampPanToBounds, createPanEdgeSessionManager } from '../utils/imageModalPanBoundsUtils';
import { getViewportCentrePoint } from '../utils/imageModalZoomGeometryUtils';
import { clampZoomScale, getNextImageModalZoomScale, getNextZoomScale } from '../utils/imageModalZoomScaleUtils';
import {
    getNextPannedZoomState,
    getNextZoomState,
    getNextZoomStateFromWheelDelta,
} from '../utils/imageModalZoomStateUtils';
import { ImageModalZoomTransformElements } from '../utils/imageModalZoomTransformUtils';
import {
    DEFAULT_IMAGE_MODAL_MAX_SCALE,
    DEFAULT_IMAGE_MODAL_MIN_SCALE,
    ImageModalZoomDimensions,
    ImageModalZoomState,
    resetZoomState,
} from '../utils/imageModalZoomTypes';
import { useImageModalZoomAnimation } from './useImageModalZoomAnimation';
import { useImageModalZoomStore } from './useImageModalZoomStore';

export type UseImageModalZoomResult = ImageModalZoomState &
    ImageModalZoomDimensions & {
        isPanning: boolean;
        isZoomed: boolean;
        endPan: () => void;
        panBy: (panDelta: Point) => void;
        resetZoom: () => void;
        setContentSize: (contentSize: RectSize) => void;
        setTranslation: (translation: Point) => void;
        setViewportSize: (viewportSize: RectSize) => void;
        startPan: (point: Point) => boolean;
        updatePan: (point: Point) => void;
        recordZoomAnchorPoint: (anchorPoint: Point) => void;
        zoomIn: (anchorPoint?: Point) => void;
        zoomOut: (anchorPoint?: Point) => void;
        zoomToPoint: (anchorPoint: Point, scaleDelta: number) => void;
        zoomWheelToPoint: (anchorPoint: Point, wheelDeltaY: number) => void;
        registerZoomTransformElements: (elements: ImageModalZoomTransformElements) => void;
    };

const EMPTY_SIZE: RectSize = {
    width: 0,
    height: 0,
};

/**
 * Compares viewport or content sizes without requiring a full rect.
 */
const areSizesEqual = (sizeA: RectSize, sizeB: RectSize): boolean =>
    sizeA.width === sizeB.width && sizeA.height === sizeB.height;

/**
 * Coordinates image modal zoom state, pan state, viewport sizing, and public zoom controls.
 */
const useImageModalZoom = ({
    maxScale = DEFAULT_IMAGE_MODAL_MAX_SCALE,
    minScale = DEFAULT_IMAGE_MODAL_MIN_SCALE,
}: {
    maxScale?: number;
    minScale?: number;
} = {}): UseImageModalZoomResult => {
    const [viewportSize, setViewportSizeState] = useState<RectSize>(EMPTY_SIZE);
    const [contentSize, setContentSizeState] = useState<RectSize>(EMPTY_SIZE);
    const [isPanning, setIsPanning] = useState(false);
    const previousPanPointRef = useRef<Point | null>(null);
    const isPanningRef = useRef(false);
    const lastZoomAnchorPointRef = useRef<Point | null>(null);
    const panEdgeSessionManagerRef = useRef(createPanEdgeSessionManager());
    const viewportSizeRef = useUpdatedRef(viewportSize);
    const contentSizeRef = useUpdatedRef(contentSize);
    const { zoomState, zoomStateRef, commitZoomState, updateZoomState, registerZoomTransformElements } =
        useImageModalZoomStore();

    /**
     * Records the post-zoom pan bounds that should remain reachable until the next scale change.
     */
    const commitPanEdgeSession = useCallback((translation: Point, scale: number) => {
        panEdgeSessionManagerRef.current.commit(translation, {
            scale,
            viewportSize: viewportSizeRef.current,
            contentSize: contentSizeRef.current,
        });
    }, []);

    /**
     * Clears any expanded pan bounds before a new zoom or reset starts.
     */
    const clearPanEdgeSession = useCallback(() => {
        panEdgeSessionManagerRef.current.clear();
    }, []);

    /**
     * Returns the active expanded pan bounds for panning and clamping calculations.
     */
    const getPanEdgeSession = useCallback(() => panEdgeSessionManagerRef.current.getSession(), []);

    const { animationTargetRef, animateZoomToScale, isZoomAnimating, stopZoomAnimation } = useImageModalZoomAnimation({
        clearPanEdgeSession,
        commitPanEdgeSession,
        commitZoomState,
        contentSizeRef,
        viewportSizeRef,
        zoomStateRef,
    });

    /**
     * Stores the last known viewport anchor so button zoom can use the user's recent focal point.
     */
    const recordZoomAnchorPoint = useCallback((anchorPoint: Point) => {
        lastZoomAnchorPointRef.current = anchorPoint;
    }, []);

    /**
     * Applies a target scale immediately or via the zoom animation, keeping pan edge sessions in sync.
     */
    const applyZoomToTarget = useCallback(
        (anchorPoint: Point, targetScale: number, { animate }: { animate: boolean }) => {
            recordZoomAnchorPoint(anchorPoint);

            const currentTargetScale = animationTargetRef.current?.scale ?? zoomStateRef.current.scale;
            if (targetScale === currentTargetScale) return;

            if (animate) {
                animateZoomToScale(targetScale, anchorPoint);
                return;
            }

            stopZoomAnimation();
            clearPanEdgeSession();
            updateZoomState(() => {
                const scaleDelta = targetScale - zoomStateRef.current.scale;
                const nextZoomState = getNextZoomState({
                    anchorPoint,
                    bounds: { minScale, maxScale },
                    dimensions: {
                        viewportSize: viewportSizeRef.current,
                        contentSize: contentSizeRef.current,
                    },
                    scaleDelta,
                    zoomState: zoomStateRef.current,
                });

                commitPanEdgeSession(nextZoomState.translation, nextZoomState.scale);

                return nextZoomState;
            });
        },
        [
            animateZoomToScale,
            clearPanEdgeSession,
            commitPanEdgeSession,
            maxScale,
            minScale,
            recordZoomAnchorPoint,
            stopZoomAnimation,
            updateZoomState,
        ],
    );

    /**
     * Pans by a delta, using imperative DOM updates during drag to avoid rendering on every movement.
     */
    const panBy = useCallback(
        (panDelta: Point) => {
            stopZoomAnimation();

            if (isPanningRef.current) {
                const nextZoomState = getNextPannedZoomState({
                    dimensions: {
                        viewportSize: viewportSizeRef.current,
                        contentSize: contentSizeRef.current,
                    },
                    panDelta,
                    panEdgeSession: getPanEdgeSession(),
                    zoomState: zoomStateRef.current,
                });

                commitZoomState(nextZoomState, { syncReact: false });
                return;
            }

            updateZoomState((currentZoomState) =>
                getNextPannedZoomState({
                    dimensions: {
                        viewportSize: viewportSizeRef.current,
                        contentSize: contentSizeRef.current,
                    },
                    panDelta,
                    panEdgeSession: getPanEdgeSession(),
                    zoomState: currentZoomState,
                }),
            );
        },
        [commitZoomState, stopZoomAnimation, updateZoomState],
    );

    /**
     * Stores the measured viewport size while keeping the ref available to imperative zoom maths.
     */
    const setViewportSize = useCallback((nextViewportSize: RectSize) => {
        viewportSizeRef.current = nextViewportSize;
        setViewportSizeState((currentViewportSize) =>
            areSizesEqual(currentViewportSize, nextViewportSize) ? currentViewportSize : nextViewportSize,
        );
    }, []);

    /**
     * Stores the measured content size while keeping the ref available to imperative zoom maths.
     */
    const setContentSize = useCallback((nextContentSize: RectSize) => {
        contentSizeRef.current = nextContentSize;
        setContentSizeState((currentContentSize) =>
            areSizesEqual(currentContentSize, nextContentSize) ? currentContentSize : nextContentSize,
        );
    }, []);

    /**
     * Re-clamps zoom state after size or scale bounds change, unless a gesture or animation owns the state.
     */
    useEffect(() => {
        if (isPanningRef.current || isZoomAnimating || animationTargetRef.current !== null) return;

        const currentZoomState = zoomStateRef.current;
        const clampedScale = clampZoomScale(currentZoomState.scale, { minScale, maxScale });
        const dimensions = {
            scale: clampedScale,
            viewportSize,
            contentSize,
        };
        const clampedTranslation = clampPanToBounds(currentZoomState.translation, dimensions, {
            panEdgeSession: getPanEdgeSession(),
        });

        panEdgeSessionManagerRef.current.refreshForViewport(clampedTranslation, dimensions);

        const hasReachedLimits =
            clampedScale === currentZoomState.scale && equals(clampedTranslation, currentZoomState.translation);

        if (hasReachedLimits) return;

        commitZoomState(
            {
                scale: clampedScale,
                translation: clampedTranslation,
            },
            { syncReact: true },
        );
    }, [commitZoomState, contentSize, getPanEdgeSession, isZoomAnimating, maxScale, minScale, viewportSize]);

    /**
     * Resets zoom, pan, animation, and remembered anchor state back to rest.
     */
    const resetZoom = useCallback(() => {
        stopZoomAnimation();
        clearPanEdgeSession();
        previousPanPointRef.current = null;
        isPanningRef.current = false;
        lastZoomAnchorPointRef.current = null;
        setIsPanning(false);
        updateZoomState(resetZoomState());
    }, [clearPanEdgeSession, stopZoomAnimation, updateZoomState]);

    /**
     * Sets pan translation directly while respecting the active pan bounds.
     */
    const setTranslation = useCallback(
        (nextTranslation: Point) => {
            stopZoomAnimation();
            updateZoomState((currentZoomState) => {
                const clampedTranslation = clampPanToBounds(
                    nextTranslation,
                    {
                        scale: currentZoomState.scale,
                        viewportSize: viewportSizeRef.current,
                        contentSize: contentSizeRef.current,
                    },
                    { panEdgeSession: getPanEdgeSession() },
                );

                if (equals(clampedTranslation, currentZoomState.translation)) {
                    return currentZoomState;
                }

                return {
                    ...currentZoomState,
                    translation: clampedTranslation,
                };
            });
        },
        [stopZoomAnimation, updateZoomState],
    );

    /**
     * Applies an immediate zoom delta around a viewport anchor point.
     */
    const zoomToPoint = useCallback(
        (anchorPoint: Point, scaleDelta: number) => {
            const targetScale = getNextZoomScale({
                currentScale: zoomStateRef.current.scale,
                scaleDelta,
                minScale,
                maxScale,
            });

            applyZoomToTarget(anchorPoint, targetScale, { animate: false });
        },
        [applyZoomToTarget, maxScale, minScale],
    );

    /**
     * Converts a wheel delta into an animated zoom target around a viewport anchor point.
     */
    const zoomWheelToPoint = useCallback(
        (anchorPoint: Point, wheelDeltaY: number) => {
            const current = zoomStateRef.current;
            const currentTargetScale = animationTargetRef.current?.scale ?? current.scale;
            const nextTargetState = getNextZoomStateFromWheelDelta({
                anchorPoint,
                bounds: { minScale, maxScale },
                dimensions: {
                    viewportSize: viewportSizeRef.current,
                    contentSize: contentSizeRef.current,
                },
                wheelDeltaY,
                zoomState: { ...current, scale: currentTargetScale },
            });

            applyZoomToTarget(anchorPoint, nextTargetState.scale, { animate: true });
        },
        [applyZoomToTarget, maxScale, minScale],
    );

    /**
     * Steps keyboard/button zoom using the canvas focal-length curve.
     */
    const stepZoom = useCallback(
        (direction: 1 | -1, anchorPoint?: Point) => {
            const currentTargetScale = animationTargetRef.current?.scale ?? zoomStateRef.current.scale;
            const hasStartedZoom = currentTargetScale > minScale;
            const nextScale = getNextImageModalZoomScale({
                currentScale: currentTargetScale,
                direction,
                minScale,
                maxScale,
            });

            // While a zoom animation is already running, keep its anchor so a rapid sequence of presses
            // eases towards the new target from the same focal point instead of drifting.
            const anchor =
                direction === -1
                    ? getViewportCentrePoint(viewportSizeRef.current)
                    : (anchorPoint ??
                      animationTargetRef.current?.anchorPoint ??
                      (hasStartedZoom ? lastZoomAnchorPointRef.current : null) ??
                      getViewportCentrePoint(viewportSizeRef.current));

            applyZoomToTarget(anchor, nextScale, { animate: true });
        },
        [applyZoomToTarget, maxScale, minScale],
    );

    /**
     * Zooms in by one keyboard/button step.
     */
    const zoomIn = useCallback((anchorPoint?: Point) => stepZoom(1, anchorPoint), [stepZoom]);

    /**
     * Zooms out by one keyboard/button step.
     */
    const zoomOut = useCallback((anchorPoint?: Point) => stepZoom(-1, anchorPoint), [stepZoom]);

    /**
     * Starts a drag pan gesture when the image is zoomed beyond rest scale.
     */
    const startPan = useCallback(
        (point: Point): boolean => {
            if (zoomStateRef.current.scale <= minScale) return false;

            stopZoomAnimation();
            previousPanPointRef.current = point;
            isPanningRef.current = true;
            setIsPanning(true);
            return true;
        },
        [minScale, stopZoomAnimation],
    );

    /**
     * Updates an active drag pan gesture from the latest pointer position.
     */
    const updatePan = useCallback(
        (point: Point) => {
            if (!previousPanPointRef.current) return;

            const panDelta = difference(previousPanPointRef.current, point);

            previousPanPointRef.current = point;
            panBy(panDelta);
        },
        [panBy],
    );

    /**
     * Commits the imperative drag pan state back to React when the gesture ends.
     */
    const endPan = useCallback(() => {
        const committed = zoomStateRef.current;
        previousPanPointRef.current = null;
        commitZoomState(committed);
        isPanningRef.current = false;
        setIsPanning(false);
    }, [commitZoomState]);

    return {
        ...zoomState,
        viewportSize,
        contentSize,
        isPanning,
        isZoomed: zoomState.scale > minScale || isZoomAnimating,
        setViewportSize,
        setContentSize,
        setTranslation,
        panBy,
        recordZoomAnchorPoint,
        registerZoomTransformElements,
        zoomToPoint,
        zoomWheelToPoint,
        zoomIn,
        zoomOut,
        resetZoom,
        startPan,
        updatePan,
        endPan,
    };
};

export default useImageModalZoom;
